/**
 * Business events.
 *
 * Reports the two commercially meaningful storefront actions:
 *   - Add to cart -> customer_add_to_cart
 *   - Buy it now  -> customer_checkout_* (the click navigates away immediately,
 *                    so the payload is pushed synchronously on click, before the
 *                    redirect happens)
 *
 * Scope resolution
 * ----------------
 * The quick-add modal only injects the *inner HTML* of the section's
 * [data-api-content] wrapper (theme.dev.js `modalCreate`,
 * product-variant-small.js `replaceQuickAdd`), so [data-api-content] itself is
 * never part of the live DOM. The product page does not render it at all.
 * Scoping every lookup to [data-api-content] therefore matched nothing, which is
 * why clicks on "Add To Cart" / "Buy It Now" produced no events.
 *
 * The scopes used below mirror what the theme's own components rely on, e.g.
 * checkout-btn resolves its event scope with
 * closest('[data-quick-add-modal], [data-section-type]') (global.js).
 */
(function (window, document) {
  'use strict';

  if (window.__brandBusinessEventInitialized) return;
  window.__brandBusinessEventInitialized = true;

  var QUICK_ADD_SCOPE_SELECTOR = [
    '[data-quick-add-modal]',
    '[data-api-content]',
    '[data-upsell-products]',
    '[data-quick-add-holder]'
  ].join(', ');

  var PRODUCT_SCOPE_SELECTOR = 'product-component, [data-section-type="product"]';
  var STICKY_BAR_SELECTOR = '#cart-bar';

  var ADD_TO_CART_SELECTOR = '[data-add-to-cart], [data-add-to-cart-bar]';

  var CHECKOUT_SELECTOR = [
    'checkout-btn',
    '.shopify-payment-button__button',
    'shopify-accelerated-checkout button'
  ].join(', ');
  var CHECKOUT_SKIP_SELECTOR = '.shopify-payment-button__more-options';

  var SOURCES = {
    quickAdd: 'quick_add',
    productPage: 'product_page',
    stickyBar: 'product_page_sticky'
  };

  // How long a click waits for the theme "added" event before the cart is read
  // back directly.
  var ADD_SETTLE_TIMEOUT = 4000;

  // product.js forwards a sticky-bar click onto the product form button, so one
  // add is observed twice inside this window.
  var FORWARDED_CLICK_WINDOW = 2000;

  var MAX_PENDING_ADDS = 10;
  var SEARCH_PENDING_KEY = 'business_event_pending_search';

  var pendingAdds = [];

  function moneyFromCents(value) {
    var cents = Number(value);
    return Number.isFinite(cents) ? Number((cents / 100).toFixed(2)) : 0;
  }

  function getCurrency(fallback) {
    return fallback
      || window.brandTracking?.getCurrency?.()
      || window.gaGlobalVars?.currency
      || window.Shopify?.currency?.active
      || '';
  }

  function getPageType() {
    return window.gaGlobalVars?.page_type || '';
  }

  function getPageName() {
    return window.gaGlobalVars?.page_name || '';
  }

  function parseProduct(root) {
    var productJson = root?.querySelector('[data-product-json]');
    if (!productJson) return null;

    try {
      return JSON.parse(productJson.textContent);
    } catch (error) {
      console.error('[BusinessEvent] Invalid product JSON.', error);
      return null;
    }
  }

  function getSelectedVariantId(element, root) {
    var dataId = Number(element?.dataset?.id || element?.dataset?.variantId);
    if (dataId) return dataId;

    var formIdInput = element?.form?.elements?.namedItem('id');
    var formVariantId = Number(formIdInput?.value);
    if (formVariantId) return formVariantId;

    return Number(root?.querySelector('input[name="id"]')?.value) || 0;
  }

  function getQuantity(element, root) {
    var dataQuantity = Number(element?.dataset?.number);
    if (dataQuantity > 0) return dataQuantity;

    var ownForm = element?.form || element?.closest('form');
    var ownFormQuantity = ownForm?.querySelector('.cart__quantity-field, input[name="quantity"]');
    var ownFormValue = Number(ownFormQuantity?.value);
    if (ownFormValue > 0) return ownFormValue;

    var scopedQuantity = root?.querySelector('.cart__quantity-field, input[name="quantity"]');
    var scopedValue = Number(scopedQuantity?.value);
    if (scopedValue > 0) return scopedValue;

    var dialogQuantity = Number(document.querySelector('#dialog-quantity-number')?.value);
    return dialogQuantity > 0 ? dialogQuantity : 1;
  }

  function getVariantLabel(options, title) {
    var values = Array.isArray(options) ? options.filter(Boolean) : [];
    if (values.length === 1 && values[0] === 'Default Title') return '';
    if (values.length) return values.join(', ');
    if (!title || title === 'Default Title') return '';
    return String(title).split(' / ').join(', ');
  }

  function buildProductItem(product, variant, quantity) {
    if (!product || !variant) return null;

    return {
      item_id: variant.sku || variant.id,
      item_name: product.title || '',
      item_category: product.product_type || '',
      price: moneyFromCents(variant.price),
      item_variant: getVariantLabel(variant.options, variant.title),
      quantity: quantity
    };
  }

  function buildCartItem(item) {
    var variantOptions = Array.isArray(item.variant_options)
      ? item.variant_options
      : null;

    return {
      item_id: item.sku || item.variant_id || item.id,
      item_name: item.product_title || item.title || '',
      item_category: item.product_type || '',
      price: moneyFromCents(item.final_price ?? item.price),
      item_variant: getVariantLabel(variantOptions, item.variant_title),
      quantity: Number(item.quantity) || 0
    };
  }

  function track(payload) {
    if (!window.brandTracking || typeof window.brandTracking.track !== 'function') {
      console.warn('[BusinessEvent] Tracking is unavailable.');
      return null;
    }

    return window.brandTracking.track(payload);
  }

  function readPendingSearch() {
    try {
      var value = window.sessionStorage?.getItem(SEARCH_PENDING_KEY);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  }

  function writePendingSearch(value) {
    try {
      window.sessionStorage?.setItem(SEARCH_PENDING_KEY, JSON.stringify(value));
    } catch (error) {
      // sessionStorage can be unavailable in privacy modes.
    }
  }

  function removePendingSearch() {
    try {
      window.sessionStorage?.removeItem(SEARCH_PENDING_KEY);
    } catch (error) {
      // sessionStorage can be unavailable in privacy modes.
    }
  }

  function rememberSearchClick(button) {
    var input = button.form?.querySelector('input[name="q"]');
    var searchItem = input?.value?.trim();
    if (!searchItem) return;

    var pageType = button.dataset.pageType || getPageType();
    writePendingSearch({
      event: 'customer_click_search_button',
      page_type: pageType,
      event_label: pageType + '_click_search_button',
      search_item: searchItem,
      page_location: window.location.href
    });
  }

  function reportPendingSearch() {
    var resultRoot = document.querySelector('[data-business-search][data-search-performed="true"]');
    if (!resultRoot) return;

    var pending = readPendingSearch();
    if (!pending) return;

    removePendingSearch();

    var resultTerm = resultRoot.querySelector('input[name="q"]')?.value?.trim();
    if (!resultTerm || resultTerm !== pending.search_item) return;

    pending.search_result = resultRoot.dataset.searchResult === 'yes' ? 'yes' : 'no';
    track(pending);
  }

  function getCartUrl() {
    var cartUrl = window.theme?.routes?.cart_url;
    if (cartUrl) return cartUrl.replace(/\.js$/, '') + '.js';

    var root = window.Shopify?.routes?.root || '/';
    return root.replace(/\/$/, '') + '/cart.js';
  }

  async function readCart() {
    try {
      var response = await window.fetch(getCartUrl(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Cart request failed with status ' + response.status);

      return await response.json();
    } catch (error) {
      console.error('[BusinessEvent] Unable to read the cart.', error);
      return null;
    }
  }

  function findAddedLineItem(cart, entry) {
    return (cart?.items || []).find(function (item) {
      return Number(item.variant_id) === Number(entry.variantId)
        && Number(item.quantity) >= entry.quantity;
    });
  }

  function trackAddToCart(entry, cart, confirmedBy) {
    var payload = {
      event: 'customer_add_to_cart',
      page_type: getPageType(),
      page_name: getPageName(),
      event_label: entry.source === SOURCES.quickAdd
        ? 'quick_add_to_cart'
        : entry.source + '_add_to_cart',
      ecommerce: {
        value: moneyFromCents(cart.total_price),
        currency: getCurrency(cart.currency),
        items: (cart.items || []).map(buildCartItem)
      },
      add_to_cart_source: entry.source
    };

    if (confirmedBy) payload.add_to_cart_confirmed_by = confirmedBy;
    track(payload);
  }

  function resolveContext(element) {
    var quickAddRoot = element.closest(QUICK_ADD_SCOPE_SELECTOR);
    if (quickAddRoot) return { root: quickAddRoot, source: SOURCES.quickAdd };

    var productRoot = element.closest(PRODUCT_SCOPE_SELECTOR);
    if (productRoot) {
      return {
        root: productRoot,
        source: element.closest(STICKY_BAR_SELECTOR) ? SOURCES.stickyBar : SOURCES.productPage
      };
    }

    var fallbackRoot = element.closest('#cart-bar, [data-section-type], form[action*="/cart/add"]');
    if (fallbackRoot) return { root: fallbackRoot, source: SOURCES.productPage };

    console.warn('[BusinessEvent] No product scope found for the clicked button.', element);
    return null;
  }

  function isTrackableCheckoutButton(button) {
    if (button.matches(CHECKOUT_SKIP_SELECTOR) || button.closest(CHECKOUT_SKIP_SELECTOR)) return false;
    if (button.matches('.is-disabled, [aria-disabled="true"]')) return false;

    return !button.disabled;
  }

  function trackCheckoutIntent(button, context) {
    var product = parseProduct(context.root);
    var variantId = getSelectedVariantId(button, context.root);
    var variant = product?.variants?.find(function (item) {
      return Number(item.id) === variantId;
    });
    var quantity = getQuantity(button, context.root);
    var item = buildProductItem(product, variant, quantity);

    if (!item) {
      console.warn('[BusinessEvent] Buy-now click skipped: variant', variantId, 'is missing from the product JSON.');
      return;
    }

    var isQuickAdd = context.source === SOURCES.quickAdd;

    track({
      event: isQuickAdd ? 'customer_checkout_from_quick_add' : 'customer_checkout_from_product_page',
      page_type: getPageType(),
      page_name: getPageName(),
      event_label: isQuickAdd ? 'quick_add_click_buy_it_now' : context.source + '_click_buy_it_now',
      ecommerce: {
        value: Number((item.price * quantity).toFixed(2)),
        currency: getCurrency(),
        items: [item]
      },
      checkout_method: 'checkout_page',
      payment_source: context.source
    });
  }

  function isForwardedDuplicate(entry) {
    var last = pendingAdds[pendingAdds.length - 1];
    if (!last) return false;
    if (Number(last.variantId) !== Number(entry.variantId)) return false;

    return last.forwarded !== entry.forwarded && entry.time - last.time < FORWARDED_CLICK_WINDOW;
  }

  function scheduleAddVerification(entry) {
    entry.timer = window.setTimeout(function () {
      var index = pendingAdds.indexOf(entry);
      if (index === -1) return;

      pendingAdds.splice(index, 1);
      verifyAddFromCart(entry);
    }, ADD_SETTLE_TIMEOUT);
  }

  async function verifyAddFromCart(entry) {
    var cart = await readCart();
    if (!cart) return;

    if (!findAddedLineItem(cart, entry)) {
      console.warn('[BusinessEvent] Add to cart was not confirmed for variant', entry.variantId);
      return;
    }

    trackAddToCart(entry, cart, 'cart_read');
  }

  function rememberAddToCart(button, context, event) {
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    var variantId = getSelectedVariantId(button, context.root);
    if (!variantId) {
      console.warn('[BusinessEvent] Add-to-cart click skipped: no variant id found.', button);
      return;
    }

    var entry = {
      variantId: variantId,
      quantity: getQuantity(button, context.root),
      source: context.source,
      root: context.root,
      forwarded: event.isTrusted === false,
      time: Date.now()
    };

    if (isForwardedDuplicate(entry)) {
      // The trusted click owns the label of the forwarded twin.
      var last = pendingAdds[pendingAdds.length - 1];
      if (!entry.forwarded) last.source = entry.source;
      return;
    }

    pendingAdds.push(entry);
    if (pendingAdds.length > MAX_PENDING_ADDS) pendingAdds.shift();

    scheduleAddVerification(entry);
  }

  async function onProductAdded() {
    var entry = pendingAdds.shift();
    if (!entry) return;

    if (entry.timer) window.clearTimeout(entry.timer);

    var cart = await readCart();
    if (!cart) return;

    // A cart rebuild can also happen for unrelated mutations, so only report
    // when the clicked variant is really in the cart.
    if (!findAddedLineItem(cart, entry)) {
      console.warn('[BusinessEvent] Cart rebuild without the clicked variant; event skipped.', entry.variantId);
      return;
    }

    trackAddToCart(entry, cart, 'theme_event');
  }

  function clearPendingAdds() {
    pendingAdds.forEach(function (entry) {
      if (entry.timer) window.clearTimeout(entry.timer);
    });
    pendingAdds = [];
  }

  function onDocumentClick(event) {
    if (!(event.target instanceof Element)) return;

    var searchButton = event.target.closest('[data-business-search-submit]');
    if (searchButton) {
      rememberSearchClick(searchButton);
      return;
    }

    var checkoutButton = event.target.closest(CHECKOUT_SELECTOR);
    if (checkoutButton) {
      if (!isTrackableCheckoutButton(checkoutButton)) return;

      var checkoutContext = resolveContext(checkoutButton);
      if (checkoutContext) trackCheckoutIntent(checkoutButton, checkoutContext);
      return;
    }

    var addToCartButton = event.target.closest(ADD_TO_CART_SELECTOR);
    if (!addToCartButton) return;

    var addContext = resolveContext(addToCartButton);
    if (addContext) rememberAddToCart(addToCartButton, addContext, event);
  }

  // Capture the click before the theme's button handler synchronously disables
  // the add-to-cart button while its request is in flight.
  document.addEventListener('click', onDocumentClick, true);
  document.addEventListener('theme:product:added', onProductAdded);
  document.addEventListener('theme:product:add-error', clearPendingAdds);
  reportPendingSearch();
})(window, document);
